// src/components/Modal.jsx
//
// Shared accessible modal shell.
//
// Props
//   isOpen              boolean   — controls mount + animation
//   onClose             fn        — called on Escape, backdrop click, FocusTrap deactivate
//   title               string    — used as aria-label on the dialog element
//   size                'sm'|'md'|'lg'   default 'md'
//   scrollable          boolean   default true   — false for modals with a sticky footer (flex layout)
//   disableBackdropClose boolean  default false
//   escapeDeactivates   boolean  default true  — false for non-dismissable modals (e.g. onboarding)
//   children            node
//
// disableBackdropClose mapping (from audit):
//   false (default)  — CalendarSelector, VennLinkShareModal, EmailSignatureGenerator
//   true             — NetworkTab invite form, StakeholdersTab invite form,
//                      HubSpot picker, GrantConsentModal, any destructive-action confirm
//
// Accessibility:
//   • focus-trap-react traps keyboard focus inside the panel while open
//   • Default initialFocus (omitted) → first tabbable element receives focus on open
//   • Escape key deactivates FocusTrap, which calls onDeactivate → onClose
//   • role="dialog" + aria-modal="true" + aria-label={title}
//   • returnFocusOnDeactivate restores focus to the trigger element on close
//
// Animation:
//   • DURATION mirrors --duration-normal (400ms) — update both if changed
//   • Two-frame RAF pattern ensures CSS transition fires after initial paint
//   • Component stays mounted during exit animation; unmounts after DURATION ms

import { useEffect, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import styles from './Modal.module.css';

const DURATION = 400; // ⚠ mirrors --duration-normal in vennio-tokens.css

export default function Modal({
  isOpen,
  onClose,
  title,
  size = 'md',
  scrollable = true,
  disableBackdropClose = false,
  escapeDeactivates = true,
  children,
}) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      // Two-frame RAF: first frame paints with opacity:0, second triggers transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (mounted) {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), DURATION);
      return () => clearTimeout(timer);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mounted) return null;

  const handleBackdropClick = (e) => {
    if (disableBackdropClose) return;
    if (e.target === e.currentTarget) onClose();
  };

  const panelClass = [
    'modal-panel',
    styles[size] ?? styles.md,
    scrollable ? styles.scrollable : styles.flexLayout,
  ].join(' ');

  return (
    <div
      className={`modal-backdrop${visible ? ' modal-visible' : ''}`}
      onClick={handleBackdropClick}
    >
      <FocusTrap
        focusTrapOptions={{
          onDeactivate: onClose,
          escapeDeactivates,
          // clickOutsideDeactivates: false — backdrop click handled by handleBackdropClick
          // to avoid race condition between FocusTrap deactivation and our own logic
          clickOutsideDeactivates: false,
          returnFocusOnDeactivate: true,
          // initialFocus intentionally omitted — default focuses the first tabbable element
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className={panelClass}
        >
          {children}
        </div>
      </FocusTrap>
    </div>
  );
}
